import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { catStyle } from "./categoryMeta";
import CarnivalBanner from "./CarnivalBanner";

/* guest bill session — minted by the server at checkout, kept on this device only (12h) */
export const readSess = () => {
  try {
    const s = JSON.parse(localStorage.getItem("up_sess"));
    if (!s?.token || Date.now() - (s.ts || 0) > 12 * 3600 * 1000) return null;
    return s;
  } catch { return null; }
};
export const saveSess = (token, table) => {
  if (token) localStorage.setItem("up_sess", JSON.stringify({ token, table, ts: Date.now() }));
};

const Head = ({ label }) => (
  <div className="up-head">
    <span className="up-brand">URBAN PASSPORT</span>
    <span className="up-head-label" data-testid="up-head-label">{label}</span>
  </div>
);

const Bottom = ({ children }) => <div className="up-bottom">{children}</div>;

/* friendly dead-end for made-up table numbers (/t/999, /t/abc…) — validated server-side */
export const TableNotFound = ({ table }) => (
  <>
    <div className="ut-topbar">
      <span className="ut-wordmark">Urban <span>Turban</span></span>
      <span className="ut-table-chip">TABLE ?</span>
    </div>
    <div className="up-body" style={{ textAlign: "center", paddingTop: 70 }} data-testid="up-table-not-found">
      <h1 className="up-h1">Table not found</h1>
      <p className="up-sub" style={{ marginTop: 10 }}>
        We couldn't find table “{String(table).slice(0, 12)}”. Please scan the QR code on your table —
        or browse the menu and tell us your table at checkout.
      </p>
      <button className="up-btn deepred" style={{ marginTop: 28 }} data-testid="up-tnf-menu-btn"
        onClick={() => { localStorage.removeItem("up_table"); window.location.href = "/"; }}>
        Back to menu
      </button>
    </div>
  </>
);

export const Tile = ({ src, name, className = "up-tile" }) => {
  const [broken, setBroken] = useState(!src);
  useEffect(() => setBroken(!src), [src]);
  if (broken) return <div className={className.replace("up-tile", "up-tile-fallback")}>{(name || "?").charAt(0).toUpperCase()}</div>;
  return <img src={src} alt={name} className={className} onError={() => setBroken(true)} loading="lazy" />;
};

const usd = (v) => (v == null ? "" : `$${Number(v).toFixed(2)}`);
const fmtPhone = (d) => {
  if (!d) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
};

/* 1 — LANDING */
export const Landing = ({ table, dishes, go }) => {
  const [t, setT] = useState(table || "");
  const hero = useMemo(() => (dishes || []).find((d) => d.featured && d.img) || (dishes || []).find((d) => d.img), [dishes]);
  const start = () => {
    if (!t.trim()) return toast("Enter your table number", { style: { background: "#2b2620", color: "#fff" } });
    localStorage.setItem("up_table", t.trim());
    window.history.replaceState(null, "", `/t/${encodeURIComponent(t.trim())}`);
    go("signin");
  };
  return (
    <>
      <Head label={table ? `TABLE ${table}` : "TABLE"} />
      <div className="up-body" style={{ paddingTop: 14 }}>
        <div className="up-hero">
          {hero ? <img src={hero.img} alt="" /> : <div style={{ width: "100%", height: "100%", background: "#E8743B" }} />}
          <div className="up-hero-grad" />
          <div style={{ position: "absolute", left: 20, right: 20, bottom: 20, color: "#fff" }}>
            <span className="up-pill" data-testid="up-welcome-pill">{table ? `WELCOME TO TABLE ${table.toUpperCase()}` : "WELCOME"}</span>
            <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.24em", margin: "14px 0 4px", opacity: 0.85 }}>URBAN TURBAN BY VEERGI</p>
            <h1 style={{ fontSize: 34, fontWeight: 900, lineHeight: 1.1, margin: 0 }}>Indian food,<br />Punjabi soul.</h1>
            <p style={{ fontSize: 14, opacity: 0.85, margin: "10px 0 0", maxWidth: 280 }}>Order from your table. Earn a free treat every 5 visits.</p>
          </div>
        </div>
        {!table && (
          <input data-testid="up-table-input" className="up-input" style={{ marginTop: 16 }} placeholder="Your table number"
            value={t} onChange={(e) => setT(e.target.value.slice(0, 12))} />
        )}
      </div>
      <Bottom><button data-testid="up-start-btn" className="up-btn orange" onClick={start}>START</button></Bottom>
    </>
  );
};

/* 2 — PHONE SIGN-IN */
export const SignIn = ({ table, phone, setPhone, setMe, go, api }) => {
  const [digits, setDigits] = useState(phone || "");
  const [name, setName] = useState(() => localStorage.getItem("up_name") || "");
  const [busy, setBusy] = useState(false);
  const tap = (k) => setDigits((d) => (k === "back" ? d.slice(0, -1) : (d + k).slice(0, 10)));
  const cont = async () => {
    if (digits.length !== 10) return toast("Enter your 10-digit number", { style: { background: "#2b2620", color: "#fff" } });
    setBusy(true);
    try {
      const r = await api.post("/loyalty/signin", { phone: digits, name: name.trim() || undefined });
      setPhone(digits);
      if (name.trim()) localStorage.setItem("up_name", name.trim());
      setMe(r.data);
      go("menu");
    } catch (e) {
      toast(e.response?.data?.detail || "Try again", { style: { background: "#2b2620", color: "#fff" } });
    } finally { setBusy(false); }
  };
  return (
    <>
      <Head label={table ? `TABLE ${table}` : "SIGN IN"} />
      <div className="up-body">
        <h1 className="up-h1">Your phone number</h1>
        <p className="up-sub">So your visits count toward rewards</p>
        <div className="up-card" data-testid="up-phone-display" style={{ fontSize: 22, fontWeight: 800, minHeight: 58, display: "flex", alignItems: "center" }}>
          {digits ? fmtPhone(digits) : <span style={{ color: "#c9bfa9" }}>(312) 555-01__</span>}
        </div>
        <input data-testid="up-name-input" className="up-input" style={{ marginTop: 12, fontSize: 15 }}
          placeholder="Your first & last name (for rewards)" value={name} onChange={(e) => setName(e.target.value.slice(0, 60))} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 16 }}>
          {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "back"].map((k, i) =>
            k === "" ? <span key={i} /> : (
              <button key={i} data-testid={`up-key-${k}`} className="up-key" onClick={() => tap(k)}>
                {k === "back" ? "⌫" : k}
              </button>
            ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button data-testid="up-skip-link" className="up-link" onClick={() => go("menu")}>Skip, just order</button>
        </div>
      </div>
      <Bottom><button data-testid="up-continue-btn" className="up-btn" disabled={busy} onClick={cont}>CONTINUE</button></Bottom>
    </>
  );
};

/* 3 — MENU (categories VERBATIM from the POS feed, feed order; POS variation groups = one card with option chips) */
export const VariantCard = ({ g, dishes, addVariant, initialSlug }) => {
  const [sel, setSel] = useState(
    initialSlug && g.options.some((o) => o.slug === initialSlug) ? initialSlug : g.options[0]?.slug
  );
  const opt = g.options.find((o) => o.slug === sel) || g.options[0];
  const dish = (dishes || []).find((d) => d.slug === opt.slug);
  if (!dish) return null;
  return (
    <div data-testid={`up-group-card-${g.group_id}`} className="up-card up-fade">
      <div className="up-row">
        {dish.img ? <Tile src={dish.img} name={g.parent_name} /> : <div className="ut-nametile">{g.parent_name}</div>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 15, fontWeight: 700, margin: 0, color: "#3E2A1A" }}>{g.parent_name}</p>
          <p data-testid={`up-group-from-${g.group_id}`} style={{ fontSize: 12, fontWeight: 800, color: "#B8892B", margin: "3px 0 0" }}>From {usd(g.min_price)}</p>
        </div>
        <button data-testid={`up-group-add-${g.group_id}`} className="ut-plus" onClick={() => addVariant(dish, `${g.parent_name}: ${opt.label}`)}>+</button>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
        {g.options.map((o) => (
          <button key={o.slug} data-testid={`up-opt-${g.group_id}-${o.slug}`}
            className={`ut-chip${sel === o.slug ? " on" : ""}`} onClick={() => setSel(o.slug)}>
            {o.label} · {usd(o.price)}
          </button>
        ))}
      </div>
    </div>
  );
};

export const Menu = ({ table, dishes, varGroups, catOrder, addToCart, addVariant, itemCount, me, phone, go }) => {
  const [cat, setCat] = useState(null);
  const [searching, setSearching] = useState(false);
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const { groups, groupsByCat, memberOf } = useMemo(() => {
    if (!dishes) return { groups: null, groupsByCat: {}, memberOf: {} };
    const by = {};
    dishes.forEach((d) => { (by[d.category] = by[d.category] || []).push(d); });
    const mo = {};
    const gc = {};
    (varGroups || []).forEach((g) => {
      g.options.forEach((o) => { mo[o.slug] = g; });
      if (g.category) (gc[g.category] = gc[g.category] || []).push(g);
    });
    return { groups: by, groupsByCat: gc, memberOf: mo };
  }, [dishes, varGroups]);
  const short = (d) => {
    const s = (d.description || d.category || "").split(/[.!·]/)[0].trim();
    return s.length > 44 ? s.slice(0, 44) + "…" : s;
  };
  const meta = cat ? catStyle(cat) : null;
  const catCards = useMemo(() => {
    if (!cat || !groups) return [];
    const seen = new Set();
    const cards = [];
    (groups[cat] || []).forEach((d) => {
      const g = memberOf[d.slug];
      if (g) {
        if (g.category === cat && !seen.has(g.group_id)) { seen.add(g.group_id); cards.push({ type: "group", g }); }
        return; // grouped members never show a duplicate flat card
      }
      cards.push({ type: "dish", d });
    });
    (groupsByCat[cat] || []).forEach((g) => {
      if (!seen.has(g.group_id)) { seen.add(g.group_id); cards.push({ type: "group", g }); }
    });
    return cards;
  }, [cat, groups, groupsByCat, memberOf]);

  const searchResults = useMemo(() => {
    if (!ql || !dishes) return null;
    const out = [];
    (varGroups || []).forEach((g) => {
      const matchOpts = g.options.filter((o) => {
        const d = dishes.find((x) => x.slug === o.slug);
        return o.label.toLowerCase().includes(ql) || (d && d.name.toLowerCase().includes(ql));
      });
      if (g.parent_name.toLowerCase().includes(ql) || matchOpts.length) {
        out.push({ type: "group", g, pre: matchOpts[0]?.slug });
      }
    });
    dishes.forEach((d) => {
      if (memberOf[d.slug]) return; // grouped dishes surface through their group card
      if (d.name.toLowerCase().includes(ql)) out.push({ type: "dish", d });
    });
    return out;
  }, [ql, dishes, varGroups, memberOf]);

  return (
    <>
      <div className="ut-topbar">
        <span className="ut-wordmark" data-testid="ut-wordmark">Urban <span>Turban</span></span>
        <span className="ut-table-chip" data-testid="ut-table-chip">{table ? `TABLE ${table.toUpperCase()}` : "MENU"}</span>
      </div>
      <div className="up-body">
        {!cat && (
          <>
            <CarnivalBanner />
            <h1 className="ut-menu-head" data-testid="ut-menu-heading">Menu</h1>
            <div className="ut-divider"><span className="ut-diamond" /></div>
            <div style={{ display: "flex", justifyContent: "center", margin: "0 0 16px" }}>
              {!searching ? (
                <button data-testid="ut-search-btn" aria-label="Search the menu" className="ut-search-fab"
                  onClick={() => setSearching(true)}>
                  <Search size={15} color="#B8892B" /> SEARCH
                </button>
              ) : (
                <div className="ut-search-wrap">
                  <Search size={15} color="#B8892B" />
                  <input autoFocus data-testid="ut-search-input" placeholder="Search dishes…" value={q}
                    onChange={(e) => setQ(e.target.value)} />
                  <button data-testid="ut-search-clear" aria-label="Clear search"
                    onClick={() => { setQ(""); setSearching(false); }}><X size={16} /></button>
                </div>
              )}
            </div>
            {!groups && <p className="up-sub" style={{ textAlign: "center" }}>Loading the menu…</p>}
            {groups && ql && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="ut-search-results">
                {searchResults.map((c) =>
                  c.type === "group" ? (
                    <VariantCard key={`g-${c.g.group_id}-${c.pre || ""}`} g={c.g} dishes={dishes} addVariant={addVariant} initialSlug={c.pre} />
                  ) : (
                    <div key={c.d.slug} data-testid={`up-menu-card-${c.d.slug}`} className="up-card up-row up-fade">
                      {c.d.img ? <Tile src={c.d.img} name={c.d.name} /> : <div className="ut-nametile">{c.d.name}</div>}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 15, fontWeight: 700, margin: 0, color: "#3E2A1A" }}>{c.d.name}</p>
                        <p style={{ fontSize: 12, color: "#9a8f7c", margin: "2px 0 0" }}>{c.d.category}</p>
                        <p style={{ fontSize: 13, fontWeight: 800, color: "#B8892B", margin: "4px 0 0" }}>{usd(c.d.price)}</p>
                      </div>
                      <button data-testid={`up-add-${c.d.slug}`} className="ut-plus" onClick={() => addToCart(c.d)}>+</button>
                    </div>
                  ))}
                {!searchResults.length && (
                  <p className="up-sub" style={{ textAlign: "center", marginTop: 10 }} data-testid="ut-search-empty">
                    No dishes match “{q.trim()}”
                  </p>
                )}
              </div>
            )}
            {groups && !ql && (
              <button data-testid="build-meal-tile" onClick={() => go("build")}
                style={{ display: "block", width: "100%", textAlign: "left", background: "#FFFDF8",
                  border: "1.5px solid #B8892B", borderRadius: 16, padding: "16px 18px", marginBottom: 16,
                  cursor: "pointer", fontFamily: "inherit", boxShadow: "0 1px 6px rgba(62,42,26,0.06)" }}>
                <span style={{ display: "block", fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: "#B8892B" }}>MADE YOUR WAY</span>
                <span style={{ display: "block", fontFamily: '"Yeseva One", serif', fontSize: 20, color: "#C62828", margin: "3px 0 2px" }}>Build your meal</span>
                <span style={{ display: "block", fontSize: 12, color: "#9a8f7c" }}>Thali · Bowl · Single Curry · Handi — priced by the kitchen →</span>
              </button>
            )}
            {groups && !ql && (
              <div className="ut-grid" data-testid="ut-category-grid">
                {(catOrder || []).filter((c) => (groups[c] || []).length).map((key) => {
                  const { hero, color, Icon, tag } = catStyle(key);
                  return (
                    <button key={key} data-testid={`ut-cat-${key.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}
                      className={`ut-tile up-fade${hero ? " ut-hero" : ""}`} style={{ "--jc": color }} onClick={() => setCat(key)}>
                      <span className="ut-scallop" style={{ display: "block" }} />
                      {hero ? (
                        <span className="ut-hero-inner" style={{ display: "block" }}>
                          {tag && <span className="ut-best">{tag}</span>}
                          <Icon size={34} color="#FFF6E6" strokeWidth={2.4} style={{ margin: "4px auto 0", display: "block" }} />
                          <span className="ut-tile-name" style={{ display: "block" }}>{key}</span>
                          <span className="ut-tile-count">{groups[key].length} dishes</span>
                        </span>
                      ) : (
                        <>
                          <Icon size={28} color={color} strokeWidth={2.6} style={{ margin: "4px auto 0", display: "block" }} />
                          <span className="ut-tile-name" style={{ display: "block" }}>{key}</span>
                          <span className="ut-tile-count">{groups[key].length} dishes</span>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {!ql && (
              <div style={{ textAlign: "center", marginTop: 18 }}>
                <button data-testid="up-rewards-link" className="up-link" onClick={() => go("rewards")}>
                  {phone && me?.first_name ? `Hi ${me.first_name} — your rewards →` : "Rewards →"}
                </button>
                {/* Carnival/Arcade entry MOVED to the top banner (owner, June 2026) — old bottom link hidden, /experience/home stays routable */}
              </div>
            )}
          </>
        )}
        {cat && meta && (
          <div className="up-fade">
            <button data-testid="ut-back-btn" className="ut-back" onClick={() => setCat(null)}>← All categories</button>
            <div className="ut-cat-head" style={{ "--jc": meta.color }}>
              <h2 className="ut-cat-title" data-testid="ut-cat-title">{cat}</h2>
              <span className="ut-rule" />
            </div>
            <p className="up-sub" style={{ margin: "4px 0 14px" }}>{(groups[cat] || []).length} dishes</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {catCards.map((c) =>
                c.type === "group" ? (
                  <VariantCard key={`g-${c.g.group_id}`} g={c.g} dishes={dishes} addVariant={addVariant} />
                ) : (
                  <div key={c.d.slug} data-testid={`up-menu-card-${c.d.slug}`} className="up-card up-row up-fade">
                    {c.d.img ? <Tile src={c.d.img} name={c.d.name} />
                      : <div className="ut-nametile">{c.d.name}</div>}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 15, fontWeight: 700, margin: 0, color: "#3E2A1A" }}>{c.d.name}</p>
                      <p style={{ fontSize: 12, color: "#9a8f7c", margin: "2px 0 0" }}>{short(c.d)}</p>
                      <p data-testid={`up-price-${c.d.slug}`} style={{ fontSize: 13, fontWeight: 800, color: "#B8892B", margin: "4px 0 0" }}>{usd(c.d.price)}</p>
                    </div>
                    <button data-testid={`up-add-${c.d.slug}`} className="ut-plus" onClick={() => addToCart(c.d)}>+</button>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
      {itemCount > 0 && (
        <Bottom>
          <button data-testid="up-view-order-btn" className="up-btn" onClick={() => go("cart")}>
            View order · {itemCount} {itemCount === 1 ? "item" : "items"}
          </button>
        </Bottom>
      )}
    </>
  );
};

/* 4 — CART */
const OPEN = ["waiting_for_waiter", "waiter_reviewing", "confirmed", "pos_failed", "sent_to_pos", "preparing", "ready", "served", "payment_requested", "paid"];
export const Cart = ({ table, cart, setQty, setCart, itemCount, phone, go, userId, api }) => {
  const [notes, setNotes] = useState("");
  const [tbl, setTbl] = useState("");
  const [busy, setBusy] = useState(false);
  const items = Object.values(cart);
  const send = async () => {
    if (!items.length) return;
    const sendTable = (table || tbl).trim().slice(0, 12);
    if (!sendTable) return toast("Enter your table number", { style: { background: "#2b2620", color: "#fff" } });
    localStorage.setItem("up_table", sendTable);
    setBusy(true);
    const key = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`).slice(0, 36);
    const lines = items.map((i, idx) => ({
      slug: i.slug, name: i.name, qty: i.qty, price: i.price, spice: i.spice || undefined,
      note: [i.note, idx === 0 && notes.trim() ? notes.trim() : null].filter(Boolean).join(" | ").slice(0, 160) || undefined,
    }));
    try {
      const st = await api.get("/dinein/status", { params: { user_id: userId } });
      const open = st.data.order && OPEN.includes(st.data.order.status);
      const sess = readSess();
      if (open) {
        const r = await api.post("/dinein/add-round", {
          user_id: userId, idempotency_key: key, items: lines, session_token: sess?.token || "" });
        saveSess(r.data.session, st.data.order.table || sendTable);
      } else {
        const r = await api.post("/dinein/submit", {
          user_id: userId, table: sendTable, items: lines, idempotency_key: key,
          session_token: sess?.token || "",
          special_instructions: notes.trim() || undefined,
          customer_name: localStorage.getItem("up_name") || undefined,
          loyalty_phone: phone || undefined,
        });
        saveSess(r.data.session, sendTable);
      }
      setCart({});
      go("sent");
    } catch (e) {
      toast(e.response?.data?.detail || "Couldn't send — ask your server", { style: { background: "#2b2620", color: "#fff" } });
    } finally { setBusy(false); }
  };
  return (
    <>
      <Head label={table ? `TABLE ${table}` : "ORDER"} />
      <div className="up-body">
        <h1 className="up-h1">Your order</h1>
        <p className="up-sub">Goes straight to the kitchen</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map((i) => (
            <div key={i.slug} data-testid={`up-cart-row-${i.slug}`} className="up-card up-row">
              <Tile src={i.image} name={i.name} className="up-tile" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 14, fontWeight: 800, margin: 0 }}>{i.name}</p>
                <p style={{ fontSize: 12, color: "#9a8f7c", margin: "2px 0 0" }}>{usd(i.price)}</p>
              </div>
              <div className="up-row" style={{ gap: 8 }}>
                <button data-testid={`up-qty-minus-${i.slug}`} className="up-qty-btn" onClick={() => setQty(i.slug, i.qty - 1)}>−</button>
                <span style={{ fontSize: 15, fontWeight: 900, width: 18, textAlign: "center" }}>{i.qty}</span>
                <button data-testid={`up-qty-plus-${i.slug}`} className="up-qty-btn" onClick={() => setQty(i.slug, i.qty + 1)}>+</button>
              </div>
            </div>
          ))}
          {!items.length && <p className="up-sub">Nothing here yet — <button className="up-link" onClick={() => go("menu")}>back to the menu</button></p>}
        </div>
        {!table && (
          <input data-testid="up-cart-table-input" className="up-input" style={{ marginTop: 14, fontSize: 15, fontWeight: 800 }}
            placeholder="Your table number (on the table sign)" inputMode="numeric"
            value={tbl} onChange={(e) => setTbl(e.target.value.slice(0, 12))} />
        )}
        <input data-testid="up-notes-input" className="up-input" style={{ marginTop: 14, fontSize: 14 }}
          placeholder="🌶️ Spice level · Allergies or notes" value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 300))} />
        <div style={{ marginTop: 16, fontSize: 13, color: "#9a8f7c" }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 2px" }}><span>Items</span><span style={{ color: "#2b2620", fontWeight: 800 }}>{itemCount}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 2px" }}>
            <span>Subtotal</span>
            <span data-testid="up-subtotal" style={{ color: "#2b2620", fontWeight: 900, fontSize: 15 }}>
              {usd(items.reduce((s, i) => s + (Number(i.price) || 0) * i.qty, 0))}
            </span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 2px" }}><span>Tax</span><span data-testid="up-tax-line">Added at payment</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 2px" }}><span>Pay</span><span>At the end, at your table</span></div>
        </div>
      </div>
      <Bottom>
        <button data-testid="up-send-btn" className="up-btn deepred" disabled={busy || !items.length} onClick={send}>Send to kitchen</button>
      </Bottom>
    </>
  );
};

/* 5 — ORDER SENT */
const STAGES = ["Sent", "Cooking", "On its way", "Enjoy"];
const stageOf = (s) => (s === "served" ? 3 : s === "ready" ? 2 : ["confirmed", "sent_to_pos", "preparing", "pos_failed"].includes(s) ? 1 : 0);
export const Sent = ({ table, phone, me, refreshMe, go, userId, api }) => {
  const [order, setOrder] = useState(null);
  useEffect(() => {
    const load = () => api.get("/dinein/status", { params: { user_id: userId } }).then((r) => setOrder(r.data.order)).catch(() => {});
    load();
    const t = setInterval(() => { load(); refreshMe(); }, 10000);
    return () => clearInterval(t);
  }, [userId, api, refreshMe]);
  const stage = order ? stageOf(order.status) : 0;
  const counted = phone && me?.counted_today;
  return (
    <>
      <Head label={table ? `TABLE ${table}` : "ORDER"} />
      <div className="up-body" style={{ textAlign: "center", paddingTop: 40 }} data-testid="up-order-sent">
        <div style={{ width: 110, height: 110, borderRadius: "50%", background: "#6BA368", color: "#fff", fontSize: 46, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}>✓</div>
        <h1 className="up-h1" style={{ marginTop: 22 }}>Order sent</h1>
        <p className="up-sub">The kitchen has it. Sit back.</p>
        <div className="up-steps">
          {STAGES.map((s, i) => (
            <div key={s} className={`up-step ${i <= stage ? "on" : ""}`} data-testid={`up-step-${i}`}>
              <div className="up-step-bar" />
              <p className="up-step-label">{s}</p>
            </div>
          ))}
        </div>
        {counted && (
          <div className="up-card up-fade" data-testid="up-visit-badge" style={{ marginTop: 22, textAlign: "left" }}>
            <p style={{ fontSize: 14, fontWeight: 900, margin: 0 }}>+1 visit counted</p>
            <p style={{ fontSize: 12, color: "#9a8f7c", margin: "3px 0 0" }}>
              {me.to_go > 0 ? `You're ${me.to_go} away from a free ${me.reward_item}` : `Your free ${me.reward_item} is unlocked — see Rewards`}
            </p>
          </div>
        )}
        {phone && (
          <button className="up-link" style={{ marginTop: 18 }} data-testid="up-sent-rewards-link" onClick={() => go("rewards")}>See your rewards →</button>
        )}
      </div>
      <Bottom>
        <button data-testid="up-pay-now-btn" className="up-btn deepred" style={{ marginBottom: 10 }} onClick={() => go("pay")}>Pay now</button>
        <button data-testid="up-pay-server-btn" className="up-btn" onClick={() => go("thanks")}>Pay with your server</button>
        <p style={{ textAlign: "center", margin: "10px 0 0" }}>
          <button data-testid="up-add-more-btn" className="up-link" onClick={() => go("menu")}>+ Add more to this order</button>
        </p>
      </Bottom>
    </>
  );
};

/* 5b — PAY AT TABLE (bill + tip → Stripe Checkout) — locked to THIS guest's session orders */
const GOLD_PAY_OPTIN = false; // giveaway is dine-in only: checkout email/opt-in hidden, code kept

export const Pay = ({ table, go, userId, api }) => {
  const [bill, setBill] = useState(null);
  const [tipPct, setTipPct] = useState(18);
  const [customTip, setCustomTip] = useState("");
  const [busy, setBusy] = useState(false);
  const [gEmail, setGEmail] = useState("");
  const [offers, setOffers] = useState(false);
  const sess = readSess();
  const effTable = table || sess?.table || "";
  const locked = !sess?.token || !effTable || (table && sess?.table && sess.table !== table);
  useEffect(() => {
    if (locked) { go("menu"); return; }  // no session on this device → back to the menu, no bill
    api.get("/dinein/bill", { params: { session: sess.token, table: effTable } })
      .then((r) => setBill(r.data)).catch(() => setBill(false));
  }, [locked, api]); // eslint-disable-line react-hooks/exhaustive-deps
  if (locked) return null;
  if (bill === false) {
    return (<>
      <Head label="PAY" />
      <div className="up-body" style={{ paddingTop: 60, textAlign: "center" }} data-testid="up-bill-empty">
        <h1 className="up-h1">You haven't ordered yet</h1>
        <p className="up-sub">Send something to the kitchen first — your bill will appear here</p>
      </div>
      <Bottom><button className="up-btn" data-testid="up-pay-back-menu" onClick={() => go("menu")}>BACK TO MENU</button></Bottom>
    </>);
  }
  const tip = bill ? (tipPct === "custom" ? Math.max(0, Number(customTip) || 0) : +(bill.subtotal * tipPct / 100).toFixed(2)) : 0;
  const total = bill ? +(bill.total + tip).toFixed(2) : 0;
  const payNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/dinein/pay/checkout", {
        user_id: userId, session_token: sess.token, table: effTable, tip, origin_url: window.location.origin,
        email: gEmail.trim(), offers_optin: offers });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast(e.response?.data?.detail || "Couldn't start payment", { style: { background: "#2b2620", color: "#fff" } });
      setBusy(false);
    }
  };
  return (
    <>
      <Head label={table ? `TABLE ${table}` : "PAY"} />
      <div className="up-body">
        <h1 className="up-h1">Your bill</h1>
        <p className="up-sub">Pay here, or with your server — your choice</p>
        {!bill && <p className="up-sub">Loading…</p>}
        {bill && (
          <>
            <div className="up-card" data-testid="up-bill-card">
              {bill.items.map((i, idx) => (
                <div key={idx} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0" }}>
                  <span style={{ fontWeight: 600 }}>{i.qty}× {i.name}</span>
                  <span style={{ fontWeight: 700 }}>${(i.price * i.qty).toFixed(2)}</span>
                </div>
              ))}
              <div style={{ borderTop: "1px solid #EFE5CE", marginTop: 8, paddingTop: 8, fontSize: 13, color: "#9a8f7c" }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span>Subtotal</span><span data-testid="up-bill-subtotal" style={{ color: "#2b2620", fontWeight: 700 }}>${bill.subtotal.toFixed(2)}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                  <span>Tax{bill.tax_rate != null ? ` (${bill.tax_rate}%)` : ""}</span>
                  <span data-testid="up-bill-tax" style={{ color: "#2b2620", fontWeight: 700 }}>${bill.tax.toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span>Tip</span><span data-testid="up-bill-tip" style={{ color: "#2b2620", fontWeight: 700 }}>${tip.toFixed(2)}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0 0", fontSize: 17 }}>
                  <span style={{ color: "#2b2620", fontWeight: 800 }}>Total</span>
                  <span data-testid="up-bill-total" style={{ color: "#B71C1C", fontWeight: 900 }}>${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
            <p style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", color: "#9a8f7c", margin: "16px 2px 8px" }}>ADD A TIP</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 8 }}>
              {(bill.tip_presets || [15, 18, 20]).map((p) => (
                <button key={p} data-testid={`up-tip-${p}`} onClick={() => setTipPct(p)}
                  style={{ padding: "11px 0", borderRadius: 14, border: "none", cursor: "pointer", fontWeight: 800, fontSize: 13,
                    background: tipPct === p ? "#2b2620" : "#fff", color: tipPct === p ? "#fff" : "#2b2620", boxShadow: "0 2px 8px rgba(43,38,32,0.05)" }}>
                  {p}%
                </button>
              ))}
              <button data-testid="up-tip-custom" onClick={() => setTipPct("custom")}
                style={{ padding: "11px 0", borderRadius: 14, border: "none", cursor: "pointer", fontWeight: 800, fontSize: 12,
                  background: tipPct === "custom" ? "#2b2620" : "#fff", color: tipPct === "custom" ? "#fff" : "#2b2620", boxShadow: "0 2px 8px rgba(43,38,32,0.05)" }}>
                $…
              </button>
              <button data-testid="up-tip-none" onClick={() => { setTipPct(0); }}
                style={{ padding: "11px 0", borderRadius: 14, border: "none", cursor: "pointer", fontWeight: 800, fontSize: 12,
                  background: tipPct === 0 ? "#2b2620" : "#fff", color: tipPct === 0 ? "#fff" : "#2b2620", boxShadow: "0 2px 8px rgba(43,38,32,0.05)" }}>
                None
              </button>
            </div>
            {tipPct === "custom" && (
              <input data-testid="up-tip-custom-input" className="up-input" style={{ marginTop: 10, fontSize: 15 }} type="number" min="0"
                placeholder="Tip amount in dollars" value={customTip} onChange={(e) => setCustomTip(e.target.value)} />
            )}
            {GOLD_PAY_OPTIN && (<>
              <p style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", color: "#9a8f7c", margin: "18px 2px 8px" }}>GOLD COIN GIVEAWAY</p>
              <input data-testid="up-pay-email" className="up-input" type="email" style={{ fontSize: 14 }}
                placeholder="Email (optional) to enter the gold coin draw" value={gEmail} onChange={(e) => setGEmail(e.target.value)} />
              <label data-testid="up-pay-offers" style={{ display: "flex", alignItems: "flex-start", gap: 9, marginTop: 10, fontSize: 13, cursor: "pointer" }}>
                <input type="checkbox" checked={offers} onChange={(e) => setOffers(e.target.checked)}
                  style={{ width: 18, height: 18, accentColor: "#B8892B", flex: "none", marginTop: 1 }} />
                <span><b>Email me Urban Turban offers</b><br />
                  <span style={{ fontSize: 11, color: "#9a8f7c" }}>Optional. Never affects your chances. Details: <a className="up-link" href="/gold/rules">/gold/rules</a></span></span>
              </label>
            </>)}
          </>
        )}
      </div>
      <Bottom>
        <button data-testid="up-stripe-pay-btn" className="up-btn deepred" disabled={!bill || busy} onClick={payNow}>
          {busy ? "Opening secure payment…" : `Pay $${total.toFixed(2)}`}
        </button>
        <p style={{ textAlign: "center", fontSize: 12, color: "#9a8f7c", margin: "10px 0 0" }}>Secure card payment by Stripe · or pay with your server</p>
      </Bottom>
    </>
  );
};

/* 5c — THANK YOU (after Stripe, or when paying with the server) */
export const Thanks = ({ table, phone, setPhone, go, api }) => {
  const [st, setSt] = useState(null);
  const [email, setEmail] = useState("");
  const [sentReceipt, setSentReceipt] = useState(false);
  const [savePhone, setSavePhone] = useState("");
  const [savedPhone, setSavedPhone] = useState(false);
  const sessionId = new URLSearchParams(window.location.search).get("session_id");
  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0;
    const poll = () => api.get(`/dinein/pay/status/${sessionId}`).then((r) => {
      setSt(r.data);
      if (r.data.payment_status !== "paid" && tries++ < 8) setTimeout(poll, 2500);
    }).catch(() => {});
    poll();
    return undefined;
  }, [sessionId, api]);
  const paid = st?.payment_status === "paid";
  const sendReceipt = async () => {
    try {
      await api.post("/dinein/pay/receipt", { session_id: sessionId, email });
      setSentReceipt(true);
    } catch (e) {
      toast(e.response?.data?.detail || "Try again", { style: { background: "#2b2620", color: "#fff" } });
    }
  };
  const saveRewards = async () => {
    const digits = savePhone.replace(/\D/g, "");
    try {
      await api.post("/loyalty/signin", { phone: digits });
      setPhone(digits);
      setSavedPhone(true);
    } catch (e) {
      toast(e.response?.data?.detail || "Check the number", { style: { background: "#2b2620", color: "#fff" } });
    }
  };
  return (
    <>
      <Head label={table ? `TABLE ${table}` : "THANK YOU"} />
      <div className="up-body" style={{ textAlign: "center", paddingTop: 44 }} data-testid="up-thanks-screen">
        <div style={{ width: 110, height: 110, borderRadius: "50%", background: sessionId && !paid ? "#B8892B" : "#6BA368", color: "#fff", fontSize: 46, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}>
          {sessionId && !paid ? "…" : "✓"}
        </div>
        <h1 className="up-h1" style={{ marginTop: 22 }}>{sessionId && !paid ? "Checking your payment…" : "Thank you"}</h1>
        {paid && <p className="up-sub" data-testid="up-thanks-amount">Paid ${Number(st.amount).toFixed(2)} · Urban Turban by VeerGi</p>}
        {!sessionId && <p className="up-sub" data-testid="up-thanks-server-note">Your server will bring the bill whenever you're ready.</p>}
        {sessionId && !paid && <p className="up-sub">One moment — confirming with Stripe</p>}
        {paid && !sentReceipt && (
          <div className="up-card" style={{ marginTop: 20, textAlign: "left" }}>
            <p style={{ fontSize: 13, fontWeight: 800, margin: "0 0 8px" }}>Email receipt (optional)</p>
            <div style={{ display: "flex", gap: 8 }}>
              <input data-testid="up-receipt-email" className="up-input" style={{ fontSize: 14, padding: 12 }} placeholder="you@email.com"
                value={email} onChange={(e) => setEmail(e.target.value)} />
              <button data-testid="up-receipt-send" onClick={sendReceipt}
                style={{ background: "#B8892B", color: "#fff", border: "none", borderRadius: 14, padding: "0 16px", fontWeight: 800, fontSize: 12, cursor: "pointer" }}>
                SEND
              </button>
            </div>
          </div>
        )}
        {sentReceipt && <p className="up-sub" data-testid="up-receipt-ok" style={{ marginTop: 16 }}>Receipt on its way to {email}</p>}
        {!phone && !savedPhone && (
          <div className="up-card" data-testid="up-save-rewards-card" style={{ marginTop: 20, textAlign: "left" }}>
            <p style={{ fontSize: 13, fontWeight: 800, margin: "0 0 2px" }}>Save your rewards (optional)</p>
            <p style={{ fontSize: 12, color: "#9a8f7c", margin: "0 0 10px" }}>Enter your phone — every 5th visit is a free treat. Or just skip it.</p>
            <div style={{ display: "flex", gap: 8 }}>
              <input data-testid="up-save-phone-input" className="up-input" style={{ fontSize: 14, padding: 12 }} placeholder="(312) 555-0123" inputMode="tel"
                value={savePhone} onChange={(e) => setSavePhone(e.target.value)} />
              <button data-testid="up-save-phone-btn" onClick={saveRewards}
                style={{ background: "#2A9D8F", color: "#fff", border: "none", borderRadius: 14, padding: "0 16px", fontWeight: 800, fontSize: 12, cursor: "pointer" }}>
                SAVE
              </button>
            </div>
          </div>
        )}
        {savedPhone && <p className="up-sub" data-testid="up-save-phone-ok" style={{ marginTop: 16 }}>Visits saved to your number — see Rewards next time you order.</p>}
      </div>
      <Bottom><button data-testid="up-thanks-menu-btn" className="up-btn" onClick={() => go("menu")}>{!phone && !savedPhone ? "No thanks — back to menu" : "BACK TO MENU"}</button></Bottom>
    </>
  );
};

/* 6 — REWARDS */
const Ring = ({ cycle, needed }) => {
  const R = 86, C = 2 * Math.PI * R;
  const pct = Math.min(1, cycle / needed);
  return (
    <svg width="220" height="220" viewBox="0 0 220 220" data-testid="up-rewards-ring">
      <circle cx="110" cy="110" r={R} fill="none" stroke="#EDE2CB" strokeWidth="14" />
      <circle cx="110" cy="110" r={R} fill="none" stroke="#E8743B" strokeWidth="14" strokeLinecap="round"
        strokeDasharray={C} strokeDashoffset={C * (1 - pct)} transform="rotate(-90 110 110)" style={{ transition: "stroke-dashoffset 0.8s ease" }} />
      <text x="110" y="104" textAnchor="middle" fontSize="44" fontWeight="900" fill="#2b2620">{cycle}/{needed}</text>
      <text x="110" y="140" textAnchor="middle" fontSize="12" fontWeight="700" letterSpacing="4" fill="#9a8f7c">VISITS</text>
    </svg>
  );
};

export const Rewards = ({ table, phone, me, refreshMe, go, api }) => {
  const [showing, setShowing] = useState(null);
  if (!phone) {
    return (
      <>
        <Head label="REWARDS" />
        <div className="up-body" style={{ paddingTop: 50 }}>
          <div className="up-card up-fade" data-testid="up-rewards-guest-card" style={{ textAlign: "center", padding: 26 }}>
            <p style={{ fontSize: 40, margin: 0 }}>🎁</p>
            <h1 className="up-h1" style={{ fontSize: 24, marginTop: 10 }}>Sign in with your phone to see rewards</h1>
            <p className="up-sub" style={{ margin: "8px 0 0" }}>Every visit counts — every 5th one is a free treat on us.</p>
          </div>
        </div>
        <Bottom><button data-testid="up-rewards-signin-btn" className="up-btn" onClick={() => go("signin")}>SIGN IN</button></Bottom>
      </>
    );
  }
  const markUsed = async (r) => {
    try {
      await api.post("/loyalty/redeem", { phone, reward_id: r.reward_id });
      toast.success(`${r.item} — enjoy!`, { style: { background: "#2b2620", color: "#fff" } });
      setShowing(null);
      refreshMe();
    } catch (e) {
      toast(e.response?.data?.detail || "Already used", { style: { background: "#2b2620", color: "#fff" } });
    }
  };
  return (
    <>
      <Head label="REWARDS" />
      <div className="up-body">
        <h1 className="up-h1" data-testid="up-rewards-hi">Hi {me?.first_name || "there"}</h1>
        <p className="up-sub">Welcome back to Urban Turban</p>
        <div className="up-ring-wrap">{me && <Ring cycle={me.cycle} needed={me.needed} />}</div>
        {me && (
          <p data-testid="up-togo-line" style={{ textAlign: "center", fontSize: 14, fontWeight: 700, margin: "4px 0 18px" }}>
            {me.to_go === 1 ? `1 more visit = free ${me.reward_item}` : `${me.to_go} more visits = free ${me.reward_item}`}
          </p>
        )}
        <a data-testid="up-gold-entries-card" href="/gold/my-entries" className="up-card up-row"
          style={{ marginBottom: 10, textDecoration: "none", border: "1.5px solid #D4AF37", color: "inherit" }}>
          <div style={{ width: 40, height: 40, borderRadius: 99, flex: "none", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 9, fontWeight: 800, color: "#6B4A00", textAlign: "center",
            background: "radial-gradient(circle at 32% 28%, #FFF0B8, #E9C65A 42%, #C8962E 78%)" }}>GOLD</div>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 14, fontWeight: 900, margin: 0 }}>My coin entries</p>
            <p style={{ fontSize: 12, color: "#9a8f7c", margin: "2px 0 0" }}>Monthly gold and silver draws.</p>
          </div>
          <span style={{ fontWeight: 800, color: "#B8892B" }}>→</span>
        </a>
        {(me?.rewards || []).map((r) => (
          <div key={r.reward_id} data-testid={`up-reward-${r.reward_id}`} className="up-card up-row" style={{ marginBottom: 10, border: "2px solid #E8743B" }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 14, fontWeight: 900, margin: 0 }}>Free {r.item}</p>
              <p style={{ fontSize: 12, color: "#9a8f7c", margin: "2px 0 0" }}>Unlocked at visit {r.visit_number}</p>
            </div>
            <button data-testid={`up-redeem-${r.reward_id}`} onClick={() => setShowing(r)}
              style={{ background: "#E8743B", color: "#fff", border: "none", borderRadius: 999, padding: "10px 16px", fontSize: 11, fontWeight: 900, letterSpacing: "0.1em", cursor: "pointer" }}>
              REDEEM
            </button>
          </div>
        ))}
        <div className="up-card up-row" style={{ marginBottom: 10 }}>
          <span className="up-chip-dot" style={{ background: "#8E6CC4" }} />
          <span style={{ flex: 1, fontSize: 14, fontWeight: 700 }}>This month's rank</span>
          <span data-testid="up-rank-chip" style={{ fontSize: 14, fontWeight: 900 }}>{me?.rank ? `#${me.rank}` : "—"}</span>
        </div>
        <div className="up-card up-row">
          <span className="up-chip-dot" style={{ background: "#6BA368" }} />
          <span style={{ flex: 1, fontSize: 14, fontWeight: 700 }}>Weekly surprise</span>
          <span data-testid="up-surprise-chip" style={{ fontSize: 13, fontWeight: 800, color: "#9a8f7c", maxWidth: 140, textAlign: "right" }}>
            {me?.weekly_surprise || "Ask your server"}
          </span>
        </div>
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button data-testid="up-rules-link" className="up-link" onClick={() => go("rules")}>Rules</button>
        </div>
      </div>
      <Bottom><button data-testid="up-see-leaderboard-btn" className="up-btn pink" onClick={() => go("leaderboard")}>SEE LEADERBOARD</button></Bottom>
      {showing && (
        <div style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(43,38,32,0.65)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={() => setShowing(null)}>
          <div className="up-card" data-testid="up-redeem-sheet" style={{ width: "100%", maxWidth: 340, textAlign: "center", padding: 26 }} onClick={(e) => e.stopPropagation()}>
            <p style={{ fontSize: 11, fontWeight: 900, letterSpacing: "0.2em", color: "#B8892B", margin: 0 }}>SHOW THIS TO YOUR SERVER</p>
            <p style={{ fontSize: 28, fontWeight: 900, margin: "12px 0 2px" }}>Free {showing.item}</p>
            <p style={{ fontSize: 13, color: "#9a8f7c" }}>Your server taps the button below when they bring it.</p>
            <button data-testid="up-mark-used-btn" className="up-btn orange" style={{ marginTop: 14 }} onClick={() => markUsed(showing)}>MARK USED</button>
          </div>
        </div>
      )}
    </>
  );
};

/* 7 — LEADERBOARD */
export const Leaderboard = ({ phone, go, api }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/loyalty/leaderboard", { params: { phone } }).then((r) => setData(r.data)).catch(() => {}); }, [phone, api]);
  return (
    <>
      <Head label={(data?.month || "").toUpperCase()} />
      <div className="up-body">
        <h1 className="up-h1">Leaderboard</h1>
        <p className="up-sub">Most visits this month wins</p>
        <div className="up-prize" data-testid="up-prize-card">
          <p style={{ fontSize: 10, fontWeight: 900, letterSpacing: "0.2em", margin: 0, opacity: 0.9 }}>THIS MONTH'S PRIZE</p>
          <p style={{ fontSize: 20, fontWeight: 900, margin: "6px 0 0", lineHeight: 1.25 }}>{data?.prize_text || "Free dine-in dish of your choice"}</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
          {(data?.rows || []).map((r) => (
            <div key={r.rank} data-testid={`up-lb-row-${r.rank}`} className="up-card up-row"
              style={r.you ? { border: "2px solid #E8743B" } : undefined}>
              <span style={{ width: 22, fontSize: 15, fontWeight: 900, color: r.rank === 1 ? "#B8892B" : "#2b2620" }}>{r.rank}</span>
              <span style={{ flex: 1, fontSize: 14, fontWeight: r.you ? 900 : 700, color: r.you ? "#E8743B" : "#2b2620" }}>{r.name}</span>
              <span style={{ fontSize: 15, fontWeight: 900 }}>{r.visits}</span>
            </div>
          ))}
          {data && !data.rows.length && <p className="up-sub">No visits yet this month — be the first.</p>}
        </div>
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <button className="up-link" data-testid="up-lb-back" onClick={() => go("rewards")}>← Back to rewards</button>
        </div>
      </div>
      <Bottom>
        <div className="up-btn orange" data-testid="up-days-left" style={{ cursor: "default" }}>
          {data ? `${data.days_left} DAYS LEFT` : "…"}
        </div>
      </Bottom>
    </>
  );
};

/* 8 — RULES */
export const Rules = ({ go, api }) => {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/loyalty/rules").then((x) => setR(x.data)).catch(() => {}); }, [api]);
  return (
    <>
      <Head label="RULES" />
      <div className="up-body" data-testid="up-rules-page">
        <h1 className="up-h1">The rules</h1>
        <p className="up-sub">Short and sweet</p>
        <div className="up-card" style={{ fontSize: 14, lineHeight: 1.6 }}>
          <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{r?.rules_text || "No purchase necessary. One entry per day."}</p>
          <p style={{ margin: "12px 0 0", fontWeight: 800 }}>Every {r?.visits_needed ?? 5} visits: free {r?.reward_item || "Masala Chai"}</p>
          <p style={{ margin: "6px 0 0", fontWeight: 800 }}>Monthly prize: {r?.prize_text || "Free dine-in dish of your choice"}</p>
          {r?.weekly_surprise && <p style={{ margin: "6px 0 0" }}>This week's surprise: {r.weekly_surprise}</p>}
        </div>
      </div>
      <Bottom><button data-testid="up-rules-back-btn" className="up-btn" onClick={() => go("rewards")}>BACK</button></Bottom>
    </>
  );
};
